import { useEffect } from "react";
import styled from "styled-components";

import { useCustomerPortal } from "../../context/CustomerPortalContext";
import CustomerHero from "../../components/customer/CustomerHero";
import CustomerNavigation from "../../components/customer/CustomerNavigation";
import CustomerCard from "../../components/customer/CustomerCard";

const PLATFORM_LABELS = {
  steam: "Steam",
  psn: "PlayStation Network",
  playstation: "PlayStation Network",
  xbox: "Xbox Live",
  nintendo: "Nintendo",
  discord: "Discord",
  epic: "Epic Games",
  riot: "Riot Games",
  battlenet: "Battle.net",
  battle_net: "Battle.net",
  pokemon: "Pokémon",
  chess: "Chess.com",
  chesscom: "Chess.com",
  bga: "BoardGameArena",
  boardgamearena: "BoardGameArena",
};

export default function CustomerPlayerIdentifiersPage() {
  const {
    currentCustomer,
    authLoading,
    error,
    fetchCustomer,
    logout,
  } = useCustomerPortal();

  useEffect(() => {
    if (!currentCustomer) {
      fetchCustomer();
    }
  }, [currentCustomer, fetchCustomer]);

  if (authLoading) {
    return (
      <PageWrapper>
        <PanelInfo>Chargement des comptes joueurs...</PanelInfo>
      </PageWrapper>
    );
  }

  if (error && !currentCustomer) {
    return (
      <PageWrapper>
        <PanelMessage $error>{error}</PanelMessage>
      </PageWrapper>
    );
  }

  const identifiers = currentCustomer?.player_identifiers || [];

  return (
    <PageWrapper>
      <CustomerPanel>
        <CustomerHero customer={currentCustomer} onLogout={logout} />

        <PortalLayout>
          <CustomerNavigation />

          <ContentArea>
            <CustomerCard title="Mes comptes joueurs" full>
              <Intro>
                Retrouvez ici les identifiants associés à vos tournois,
                événements et parties organisées chez Queen's Games.
              </Intro>

              {identifiers.length > 0 ? (
                <IdentifierGrid>
                  {identifiers.map((identifier) => {
                    const key = normalizeKey(identifier.identifier_key);
                    const label =
                      identifier.identifier_label ||
                      identifier.label ||
                      PLATFORM_LABELS[key] ||
                      identifier.identifier_key ||
                      "Compte joueur";

                    return (
                      <IdentifierCard key={identifier.uuid || `${key}-${identifier.player_number}`}>
                        <PlatformBadge>{label.slice(0, 2).toUpperCase()}</PlatformBadge>

                        <IdentifierContent>
                          <strong>{label}</strong>
                          <span>
                            {identifier.player_number ||
                              identifier.number ||
                              "Identifiant non renseigné"}
                          </span>
                        </IdentifierContent>

                        <StatusBadge>Rattaché</StatusBadge>
                      </IdentifierCard>
                    );
                  })}
                </IdentifierGrid>
              ) : (
                <EmptyState>
                  Aucun compte joueur n’est encore rattaché à votre profil.
                  Un membre de l’équipe pourra les ajouter depuis l’espace
                  administration.
                </EmptyState>
              )}
            </CustomerCard>

            <CustomerCard title="Plateformes prises en charge" full>
              <PlatformGrid>
                {Object.values(PLATFORM_LABELS)
                  .filter((value, index, array) => array.indexOf(value) === index)
                  .map((platform) => (
                    <PlatformItem key={platform}>{platform}</PlatformItem>
                  ))}
              </PlatformGrid>
            </CustomerCard>
          </ContentArea>
        </PortalLayout>
      </CustomerPanel>
    </PageWrapper>
  );
}

function normalizeKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s.-]+/g, "_");
}

const PageWrapper = styled.main`
  min-height: 100dvh;
  width: 100vw;
  padding: 1.5rem;
  background: #0b1328;
  box-sizing: border-box;
  overflow-y: auto;
`;

const CustomerPanel = styled.section`
  max-width: 1280px;
  margin: 0 auto;
  color: #f7f7f7;
`;

const PortalLayout = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const ContentArea = styled.div`
  min-width: 0;
  display: grid;
  gap: 1rem;
`;

const Intro = styled.p`
  margin: 0 0 1rem;
  opacity: 0.74;
  line-height: 1.5;
`;

const IdentifierGrid = styled.div`
  display: grid;
  gap: 0.85rem;
`;

const IdentifierCard = styled.article`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.85rem;
  padding: 1rem;
  border-radius: 18px;
  background: #202a43;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const PlatformBadge = styled.div`
  width: 54px;
  height: 54px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: #fff9ea;
  color: #0b1328;
  font-weight: 950;
`;

const IdentifierContent = styled.div`
  min-width: 0;

  strong {
    display: block;
    font-weight: 950;
    margin-bottom: 0.25rem;
  }

  span {
    display: block;
    opacity: 0.76;
    word-break: break-word;
  }
`;

const StatusBadge = styled.span`
  padding: 0.35rem 0.65rem;
  border-radius: 999px;
  background: rgba(255, 249, 234, 0.12);
  color: #fff9ea;
  font-size: 0.78rem;
  font-weight: 850;
`;

const EmptyState = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: #202a43;
  opacity: 0.78;
  line-height: 1.5;
`;

const PlatformGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
`;

const PlatformItem = styled.span`
  padding: 0.55rem 0.75rem;
  border-radius: 999px;
  background: rgba(255, 249, 234, 0.1);
  color: #fff9ea;
  font-weight: 850;
  font-size: 0.82rem;
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;

const PanelInfo = styled.div`
  padding: 1rem;
  color: #f7f7f7;
  opacity: 0.75;
`;
